import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { inventoryApi } from '../../api/inventory-api-instance';
import type { SiteNoteInput } from '../../domain/siteNote';
import { summarizeSiteNotes } from '../../domain/siteNote';
import { penCountsBySite } from '../../domain/sitePenCounts';

/** One site's status log, newest first, a page at a time. */
export function useSiteNotes(customerId: string, siteId: string) {
  return useInfiniteQuery({
    queryKey: ['site-notes', customerId, siteId],
    queryFn: ({ pageParam }) => inventoryApi.listSiteNotes(customerId, siteId, pageParam),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextPageToken,
    enabled: Boolean(customerId && siteId),
  });
}

/** Each site's latest note and last contact, for site cards and panels. */
export function useSiteStatusSummaries(customerId: string) {
  const query = useQuery({
    queryKey: ['site-notes-latest', customerId],
    queryFn: () => inventoryApi.listLatestSiteNotes(customerId),
    enabled: Boolean(customerId),
  });
  return { query, bySite: summarizeSiteNotes(query.data ?? []) };
}

export function useAddSiteNote(customerId: string, siteId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (note: SiteNoteInput) => inventoryApi.addSiteNote(customerId, siteId, note),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['site-notes', customerId, siteId] }),
        queryClient.invalidateQueries({ queryKey: ['site-notes-latest'] }),
      ]);
    },
  });
}

/**
 * Each site's pens with fish and pens sending data. Leatherback asks
 * BigQuery, so this is slow-ish and loads on its own; it changes daily.
 */
export function useSitePenCounts(customerId: string) {
  const query = useQuery({
    queryKey: ['site-pen-counts', customerId],
    queryFn: () => inventoryApi.listSitePenCounts(customerId),
    enabled: Boolean(customerId),
    staleTime: 60 * 60 * 1000,
    retry: false,
  });
  return { query, bySite: penCountsBySite(query.data ?? []) };
}
